import { useState } from "react"
import { useProjects } from "@/hooks/use-projects"
import { useUpsertProjectNoteMutation } from "@/hooks/queries/use-projects"
import { supabase } from "@/lib/supabase"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { NotesSection } from "@/components/common/NotesSection"
import { ClientSelector } from "@/components/clients/client-selector"
import { useTranslation } from "react-i18next"
import { InfoTooltip } from "@/components/ui/info-tooltip"

interface CreateProjectDialogProps {
    children?: React.ReactNode
    open?: boolean
    onOpenChange?: (open: boolean) => void
    onSuccess?: () => void
}

export function CreateProjectDialog({
    children,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    onSuccess
}: CreateProjectDialogProps) {
    const { t } = useTranslation()
    const { createProject } = useProjects()
    const { mutateAsync: upsertNote } = useUpsertProjectNoteMutation()
    const [internalOpen, setInternalOpen] = useState(false)
    const [name, setName] = useState("")
    const [clientId, setClientId] = useState<string | null>(null)
    const [hourlyRate, setHourlyRate] = useState("")
    const [initialTask, setInitialTask] = useState("")
    const [initialNote, setInitialNote] = useState("")
    const [isLoading, setIsLoading] = useState(false)

    // Handle controlled vs uncontrolled state
    const isControlled = controlledOpen !== undefined
    const open = isControlled ? controlledOpen : internalOpen
    const setOpen = isControlled ? setControlledOpen : setInternalOpen

    const colors = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#d946ef', '#f43f5e']
    const getRandomColor = () => colors[Math.floor(Math.random() * colors.length)]

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name.trim()) return

        setIsLoading(true)
        try {
            const rate = parseFloat(hourlyRate) || 0
            const project = await createProject(name, getRandomColor(), rate, clientId)

            if (project) {
                // Create initial task if provided
                if (initialTask.trim()) {
                    const { error: taskError } = await supabase
                        .from('tasks')
                        .insert({
                            project_id: project.id,
                            name: initialTask.trim(),
                            status: 'active'
                        })

                    if (taskError) {
                        console.error("Error creating initial task:", taskError)
                        toast.error(t('projects.messages.taskError'))
                    }
                }

                // Create initial note if provided
                if (initialNote.trim()) {
                    await upsertNote({ projectId: project.id, content: initialNote.trim() })
                }

                toast.success(t('projects.messages.createSuccess', { name }))
                setName("")
                setClientId(null)
                setHourlyRate("")
                setInitialTask("")
                setInitialNote("")
                if (setOpen) setOpen(false)
                if (onSuccess) onSuccess()
            }
        } catch (error) {
            console.error(error)
            toast.error(t('projects.messages.createError'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t('projects.dialogs.create.title')}</DialogTitle>
                    <DialogDescription>
                        {t('projects.dialogs.create.description')}
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="details" className="w-full">
                    <div className="flex justify-center mb-4">
                        <TabsList className="bg-muted dark:bg-zinc-900 border border-border dark:border-zinc-800">
                            <TabsTrigger value="details" className="data-[state=active]:bg-background dark:data-[state=active]:bg-zinc-800 data-[state=active]:text-primary dark:data-[state=active]:text-primary">{t('projects.dialogs.tabs.details')}</TabsTrigger>
                            <TabsTrigger value="notes" className="data-[state=active]:bg-background dark:data-[state=active]:bg-zinc-800 data-[state=active]:text-primary dark:data-[state=active]:text-primary">{t('projects.dialogs.tabs.notes')}</TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="details" className="mt-0">
                        <form onSubmit={handleSubmit}>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="create-name">{t('projects.dialogs.create.nameLabel')}</Label>
                                    <Input
                                        id="create-name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="bg-background dark:bg-zinc-900 border-input dark:border-zinc-800 focus-visible:ring-primary"
                                        placeholder={t('projects.dialogs.create.namePlaceholder')}
                                        autoFocus
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="create-client" className="flex items-center">
                                        {t('projects.dialogs.create.clientLabel')}
                                        <InfoTooltip content={t('projects.dialogs.create.clientTooltip')} />
                                    </Label>
                                    <ClientSelector
                                        value={clientId}
                                        onChange={setClientId}
                                        allowNone={true}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="create-rate" className="flex items-center">
                                        {t('projects.dialogs.create.rateLabel')} (€)
                                        <InfoTooltip content={t('projects.dialogs.create.rateTooltip')} />
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="create-rate"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            value={hourlyRate}
                                            onChange={(e) => setHourlyRate(e.target.value)}
                                            className="pl-8 bg-background dark:bg-zinc-900 border-input dark:border-zinc-800 focus-visible:ring-primary"
                                        />
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                                    </div>
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="create-initial-task">{t('projects.dialogs.create.taskLabel')}</Label>
                                    <Input
                                        id="create-initial-task"
                                        value={initialTask}
                                        onChange={(e) => setInitialTask(e.target.value)}
                                        className="bg-background dark:bg-zinc-900 border-input dark:border-zinc-800 focus-visible:ring-primary"
                                        placeholder={t('projects.dialogs.create.taskPlaceholder')}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={!name.trim() || isLoading} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {isLoading ? t('projects.dialogs.create.loading') : t('projects.dialogs.create.submit')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </TabsContent>

                    <TabsContent value="notes" className="mt-0">
                        <NotesSection
                            notes={initialNote ? [{ id: 'temp', content: initialNote, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }] : []}
                            onSave={async (content) => {
                                const now = new Date().toISOString();
                                setInitialNote(content);
                                return { id: 'temp', content, created_at: now, updated_at: now };
                            }}
                            onDelete={async () => { setInitialNote(""); return true; }}
                            title={t('projects.dialogs.tasks.initialNote')}
                            placeholder={t('projects.dialogs.tasks.descPlaceholder')}
                            isSaving={false}
                        />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    )
}
